import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { ChevronLeft, ChevronRight, Search } from "lucide-react";

import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { cn } from "@/lib/utils";
import { platformApi, type Page } from "@/admin/api";

const TONES = {
  green: "bg-emerald-50 text-emerald-600 dark:bg-emerald-950/40 dark:text-emerald-400",
  amber: "bg-amber-50 text-amber-600 dark:bg-amber-950/40 dark:text-amber-400",
  red: "bg-red-50 text-red-600 dark:bg-red-950/40 dark:text-red-400",
  blue: "bg-indigo-50 text-indigo-600 dark:bg-indigo-950/40 dark:text-indigo-400",
  gray: "bg-muted text-muted-foreground",
} as const;

export function Pill({ tone, children }: { tone: keyof typeof TONES; children: React.ReactNode }) {
  return (
    <span className={cn("inline-flex rounded-full px-2 py-0.5 text-xs font-medium", TONES[tone])}>
      {children}
    </span>
  );
}

export function TenantStatusPill({ status }: { status: string }) {
  const tone = status === "active" ? "green" : status === "suspended" ? "amber" : "red";
  return <Pill tone={tone}>{status.charAt(0).toUpperCase() + status.slice(1)}</Pill>;
}

export function SearchBox({
  value,
  onChange,
  placeholder,
}: {
  value: string;
  onChange: (v: string) => void;
  placeholder: string;
}) {
  return (
    <div className="relative w-full max-w-xs">
      <Search className="pointer-events-none absolute left-2.5 top-2.5 h-4 w-4 text-muted-foreground" />
      <Input
        value={value}
        onChange={(e) => onChange(e.target.value)}
        placeholder={placeholder}
        className="pl-8"
      />
    </div>
  );
}

export function FilterButtons<T extends string>({
  options,
  value,
  onChange,
}: {
  options: { value: T; label: string }[];
  value: T;
  onChange: (v: T) => void;
}) {
  return (
    <div className="flex flex-wrap gap-1">
      {options.map((o) => (
        <Button
          key={o.value}
          size="sm"
          variant={value === o.value ? "default" : "outline"}
          onClick={() => onChange(o.value)}
        >
          {o.label}
        </Button>
      ))}
    </div>
  );
}

export function Pager<T>({ data, onPage }: { data: Page<T> | undefined; onPage: (p: number) => void }) {
  if (!data || data.pagination.total_pages <= 1) {
    return data ? (
      <p className="px-4 py-3 text-xs text-muted-foreground">
        {data.pagination.total} {data.pagination.total === 1 ? "entry" : "entries"}
      </p>
    ) : null;
  }
  const p = data.pagination;
  return (
    <div className="flex items-center justify-between px-4 py-3 text-xs text-muted-foreground">
      <span>
        Page {p.page} of {p.total_pages} · {p.total} entries
      </span>
      <div className="flex gap-1">
        <Button size="sm" variant="outline" disabled={!p.has_previous} onClick={() => onPage(p.page - 1)}>
          <ChevronLeft className="h-4 w-4" /> Previous
        </Button>
        <Button size="sm" variant="outline" disabled={!p.has_next} onClick={() => onPage(p.page + 1)}>
          Next <ChevronRight className="h-4 w-4" />
        </Button>
      </div>
    </div>
  );
}

/** A native select styled like the inputs — simpler than Radix for short lists. */
export function NativeSelect({
  value,
  onChange,
  children,
  className,
  disabled,
}: {
  value: string;
  onChange: (v: string) => void;
  children: React.ReactNode;
  className?: string;
  disabled?: boolean;
}) {
  return (
    <select
      value={value}
      disabled={disabled}
      onChange={(e) => onChange(e.target.value)}
      className={cn(
        "h-9 w-full rounded-md border border-input bg-background px-3 text-sm shadow-xs",
        "focus:outline-none focus:ring-2 focus:ring-ring disabled:opacity-50",
        className,
      )}
    >
      {children}
    </select>
  );
}

/** Pick a tenant by typing part of its name. */
export function TenantPicker({
  value,
  onChange,
  allowNone,
}: {
  value: { id: string; name: string } | null;
  onChange: (t: { id: string; name: string } | null) => void;
  allowNone?: boolean;
}) {
  const [search, setSearch] = useState("");
  const [open, setOpen] = useState(false);
  const q = useQuery({
    queryKey: ["admin", "tenant-picker", search],
    queryFn: () => platformApi.tenants({ page: 1, page_size: 8, search: search || undefined }),
    enabled: open,
    staleTime: 30_000,
  });
  if (!open) {
    return (
      <div className="flex items-center gap-2">
        <div className="flex h-9 flex-1 items-center rounded-md border border-input px-3 text-sm">
          {value ? value.name : <span className="text-muted-foreground">No tenant</span>}
        </div>
        <Button type="button" variant="outline" size="sm" onClick={() => setOpen(true)}>
          Change
        </Button>
        {allowNone && value && (
          <Button type="button" variant="ghost" size="sm" onClick={() => onChange(null)}>
            Clear
          </Button>
        )}
      </div>
    );
  }
  return (
    <div className="space-y-1 rounded-md border p-2">
      <Input autoFocus value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Search tenants…" />
      <div className="max-h-48 overflow-y-auto">
        {q.isLoading ? (
          <p className="p-2 text-xs text-muted-foreground">Loading…</p>
        ) : (q.data?.results ?? []).length === 0 ? (
          <p className="p-2 text-xs text-muted-foreground">No tenants match.</p>
        ) : (
          q.data!.results.map((t) => (
            <button
              key={t.id}
              type="button"
              className="block w-full rounded px-2 py-1.5 text-left text-sm hover:bg-muted"
              onClick={() => {
                onChange({ id: t.id, name: t.name });
                setOpen(false);
                setSearch("");
              }}
            >
              {t.name} <span className="text-xs text-muted-foreground">{t.slug}</span>
            </button>
          ))
        )}
      </div>
      <Button type="button" variant="ghost" size="sm" onClick={() => setOpen(false)}>
        Cancel
      </Button>
    </div>
  );
}
